import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatNumber, formatTaka } from '@/utils/number';
import type {
  PcmpProposalStudentClass,
  PcmpSubmissionCalculation,
} from '../../types/pcmpProposal.types';
import { buildPcmpStudentGrid } from '../../utils/pcmpStudentGrid';

interface PcmpReportStudentTableProps {
  students: PcmpProposalStudentClass[];
  calculation: PcmpSubmissionCalculation;
}

const CELL = 'border border-neutral-300 px-1.5 py-0.5';
const NUM = `${CELL} text-right tabular-nums`;

/** Class × category quantities with the total and sales-target rows. */
export function PcmpReportStudentTable({ students, calculation }: PcmpReportStudentTableProps) {
  const grid = buildPcmpStudentGrid(students, calculation);
  return (
    <table className="w-full border-collapse text-[10px]">
      <thead className="bg-neutral-100">
        <tr>
          <th className={`${CELL} text-left`}>Class</th>
          <th className={`${CELL} text-right`}>Students</th>
          {grid.categories.map((category) => (
            <th key={category.categoryId} className={`${CELL} text-right`}>
              {category.categoryName} qty
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {grid.rows.map((row) => (
          <tr key={row.classId}>
            <td className={CELL}>{row.className}</td>
            <td className={NUM}>{formatNumber(row.studentQty)}</td>
            {grid.categories.map((category) => {
              const book = row.booksByCategory.get(category.categoryId);
              return (
                <td key={category.categoryId} className={NUM}>
                  {book ? formatNumber(book.tentativePurchaseQty) : EMPTY_VALUE}
                </td>
              );
            })}
          </tr>
        ))}
        <tr className="font-semibold">
          <td className={CELL}>Total</td>
          <td className={NUM}>{formatNumber(grid.totalStudents)}</td>
          {grid.categories.map((category) => (
            <td key={category.categoryId} className={NUM}>
              {formatNumber(grid.totalsByCategory.get(category.categoryId) ?? 0)}
            </td>
          ))}
        </tr>
        <tr className="font-semibold">
          <td className={CELL}>Sales target</td>
          <td className={NUM}>{formatTaka(calculation.totalSalesTarget)}</td>
          {grid.categories.map((category) => (
            <td key={category.categoryId} className={NUM}>
              {category.salesTarget === null ? EMPTY_VALUE : formatTaka(category.salesTarget)}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}
